import type { QueryClient } from '@tanstack/react-query';
import type {
  ContributionDto,
  ContributionDetailDto,
  ContributionListResponse,
  ContributionListParams,
  CreateContributionRequest,
  RefundRequest,
  ReattributeRequest,
  RedesignateRequest,
  ResolutionChainResponse,
  ResolutionResult,
  ResolveContributorShapeRequest,
  UpdateContributionRequest,
  ValidateDraftContributionRequest,
  ValidateTransactionResponse,
  VoidRowRequest,
  TriageItem,
  FiledReportDto,
  DonorInfoRequestDto,
  RowVersion,
} from '@signalvote/shared';
import { DonorInfoRequestChannel, failureFromResponse, normalizeTransferQualification } from '@signalvote/shared';
import { every, refresh } from '@signalvote/data';
import { apiJson, apiResponse, staleVersion, type ApiErrorBody } from './apiFetch';
import {
  markConfirmed,
  markPendingReview,
  markVoided,
  markReported,
  markUnreported,
} from './sortInboxOptimistic';
import { campaignHeaders, defineQuery, defineWrite } from '@/lib/data';
import { defineStatusFlip } from './optimisticStatusFlip';
import { useFinanceContact, useFinanceContacts } from './useFinanceContacts';
import { useFinanceHealthRead } from './useFinanceDeadlines';
import { CLIENT_CONFLICT_MARKER } from '../../lib/clientConflict';

/**
 * Kind predicate selecting a Contribution row in the sort-inbox cache — the row
 * identity the optimistic patch and the concurrency-token write-back both key on.
 */
const contributionRow = (contributionId: string) => (item: TriageItem): boolean =>
  item.kind === 'Contribution' && item.contribution.id === contributionId;

/**
 * A refusal as the server's failure, read out with its sentence when it sent one and `fallback`
 * otherwise, so a caller branches on `reason` instead of a hook-local error class.
 */
export const refusal = (fallback: string) => (body: ApiErrorBody, response: Response) =>
  failureFromResponse(response, body, typeof body.error === 'string' && body.error !== '' ? body.error : fallback);

// ============================================================================
// Query Hooks
// ============================================================================

function buildContributionListUrl(params: ContributionListParams): string {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  if (params.contactId) searchParams.set('contactId', params.contactId);
  if (params.electionDesignation) searchParams.set('electionDesignation', params.electionDesignation);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  return `/api/finance/contributions?${searchParams.toString()}`;
}

/**
 * A page of the campaign's contributions. Pass `skip` until the caller wants results, as the
 * orphan-refund search does until the treasurer has typed.
 */
export const useFinanceContributions = defineQuery({
  key: (params: ContributionListParams) => ['finance', 'contributions', 'list', params],
  reads: 'GET /api/finance/contributions',
  fetch: (params, { campaignId }) =>
    apiJson<ContributionListResponse>(buildContributionListUrl(params), {
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch contributions',
    }),
});

// ============================================================================
// Mutation Hooks
// ============================================================================

export const useCreateContribution = defineWrite({
  send: (data: CreateContributionRequest) =>
    apiJson<ContributionDto>('/api/finance/contributions', {
      method: 'POST',
      json: data,
      error: refusal('Failed to create contribution'),
    }),
});

// ============================================================================
// Validate Draft Contribution
// ============================================================================

export const useValidateDraftContribution = defineWrite({
  send: (data: ValidateDraftContributionRequest) =>
    apiJson<ValidateTransactionResponse>('/api/finance/contributions/validate-draft', {
      method: 'POST',
      json: data,
      error: refusal('Failed to validate draft contribution'),
    }),
});

// ============================================================================
// Resolution Hooks (FIN-019)
// ============================================================================

/** A gift's cures, oldest first. */
export const useResolutionChain = defineQuery({
  key: (contributionId: string) => ['finance', 'contributions', 'resolutions', contributionId],
  reads: 'GET /api/finance/contributions/{id}/resolutions',
  fetch: (contributionId, { campaignId }) =>
    apiJson<ResolutionChainResponse>(`/api/finance/contributions/${contributionId}/resolutions`, {
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch resolution chain',
    }),
});

/** A cure's result plus the token its source row now has, which is what the next write to that row sends. */
export type CureResult = ResolutionResult & { concurrencyVersion: RowVersion };

async function readCure(response: Response, fallback: string): Promise<CureResult> {
  if (response.status === 409) {
    throw new Error(CLIENT_CONFLICT_MARKER);
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || fallback);
  }
  const result = (await response.json()) as ResolutionResult;
  // A split maps the new child, so the source's token rides separately; otherwise the result is the source row.
  return { ...result, concurrencyVersion: result.sourceConcurrencyVersion ?? result.contribution.concurrencyVersion };
}

const cure = (action: string) => (id: string, body: unknown) =>
  apiResponse(`/api/finance/contributions/${id}/${action}`, { method: 'POST', json: body });

export const useRefundContribution = defineWrite({
  row: 'Contribution',
  send: async (id, concurrencyVersion: RowVersion, refund: Omit<RefundRequest, 'concurrencyVersion'>) =>
    readCure(await cure('refund')(id, { ...refund, concurrencyVersion } satisfies RefundRequest), 'Failed to refund contribution'),
  // A Connect refund that loses the race to its charge.refunded webhook answers success with
  // nothing saved, so its write set is empty while health and the donor's totals have moved.
  onSuccess: (_result, _input, refreshRead) => {
    void refreshRead(useFinanceContacts, every);
    void refreshRead(useFinanceContact, every);
    void refreshRead(useFinanceHealthRead, undefined);
  },
});

export const useReattributeContribution = defineWrite({
  row: 'Contribution',
  send: async (id, concurrencyVersion: RowVersion, reattribution: Omit<ReattributeRequest, 'concurrencyVersion'>) => {
    const response = await cure('reattribute')(id, { ...reattribution, concurrencyVersion } satisfies ReattributeRequest);
    if (response.status === 422) {
      const error = await response.json().catch(() => ({}));
      throw Object.assign(new Error('GateBlocked'), { gateResult: error.gateResult });
    }
    return readCure(response, 'Failed to reattribute contribution');
  },
});

// ============================================================================
// BSW-014: Staged-page detail panel hooks
// ============================================================================

/**
 * One gift's detail panel. A failure keeps its status: a caller that renders it has to tell "no
 * such row" from "the server broke", or it tells the treasurer to hunt for a record that is right there.
 */
export const useContributionDetail = defineQuery({
  key: (contributionId: string) => ['finance', 'contributions', 'detail', contributionId],
  reads: 'GET /api/finance/contributions/{id}/detail',
  fetch: (contributionId, { campaignId }) =>
    apiJson<ContributionDetailDto>(`/api/finance/contributions/${contributionId}/detail`, {
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch contribution detail',
    }),
});

/**
 * Refetch every contribution read, for a write the server refused because the row moved on: a
 * refusal names no writes, so nothing else refreshes the row the treasurer is looking at.
 */
export function refreshContributionReads(client: QueryClient): void {
  void refresh(client, useFinanceContributions, every);
  void refresh(client, useContributionDetail, every);
  void refresh(client, useResolutionChain, every);
}

export class StagedRowUpdateError extends Error {
  code: string;
  status: number;
  /**
   * ADR-081 — present only on a `DeleteSupportsFiledReport` refusal: the already-filed
   * report the row is on. The render boundary uses it to name that report and link to it,
   * so the refusal ends on the amendment path instead of a dead end.
   */
  filedReport: FiledReportDto | null;
  constructor(
    code: string,
    status: number,
    message: string,
    filedReport: FiledReportDto | null = null,
    /** The row's version on a 409 stale write, read by the write door. */
    readonly currentVersion: number | null = null,
  ) {
    super(message);
    this.code = code;
    this.status = status;
    this.filedReport = filedReport;
  }
}

export async function throwStagedRowError(response: Response, fallbackMessage: string): Promise<never> {
  const body = await response.json().catch(() => ({}));
  // `filedReport` rides only on the ADR-081 refusal; every other code leaves it null.
  const filedReport: FiledReportDto | null =
    typeof body.filedReport?.id === 'string' && typeof body.filedReport?.name === 'string'
      ? { id: body.filedReport.id, name: body.filedReport.name }
      : null;
  throw new StagedRowUpdateError(
    body.code ?? '', response.status, body.error ?? fallbackMessage, filedReport, staleVersion(body));
}

/** The fields a contribution edit re-sends unchanged, read off the row the edit lands on. */
export type EditableContribution = Pick<
  ContributionDto,
  | 'id'
  | 'concurrencyVersion'
  | 'contactId'
  | 'contributionType'
  | 'electionDesignation'
  | 'paymentMethod'
  | 'notes'
  | 'inKindNature'
  | 'inKindDescription'
  | 'inKindFairMarketValueBasis'
  | 'transferQualification'
>;

/** The fields a contribution edit changes. Every field it leaves out is re-sent as the row has it when the edit runs. */
export type ContributionEdit = Partial<Omit<UpdateContributionRequest, 'concurrencyVersion'>>;

export const useUpdateContribution = defineWrite({
  row: 'Contribution',
  send: async (id, concurrencyVersion: RowVersion, edit: ContributionEdit, row: EditableContribution) => {
    // #711: a date/amount edit carries the recorded transfer relationship rather than sending
    // null (which restores a removed exemption), and a switch to Federal Transfer lands on an
    // explicit answer. The normalizer is the one the entry surfaces and the server use.
    const contributionType = edit.contributionType ?? row.contributionType;
    const request: UpdateContributionRequest = {
      contactId: row.contactId,
      electionDesignation: row.electionDesignation,
      paymentMethod: row.paymentMethod,
      notes: row.notes,
      // On a switch away from in-kind the server clears these whatever is sent.
      inKindNature: row.inKindNature,
      inKindDescription: row.inKindDescription,
      inKindFairMarketValueBasis: row.inKindFairMarketValueBasis,
      amount: null,
      date: null,
      ...edit,
      contributionType,
      transferQualification: normalizeTransferQualification(
        contributionType,
        edit.transferQualification ?? row.transferQualification,
      ),
      concurrencyVersion,
    };
    const response = await apiResponse(`/api/finance/contributions/${id}`, { method: 'PATCH', json: request });
    if (!response.ok) {
      await throwStagedRowError(response, 'Failed to update contribution');
    }
    return response.json() as Promise<ContributionDto>;
  },
});

/**
 * Issue #322 — the ledger contributor-shape editor. Atomically sets the shared donor's
 * person-vs-organization shape (kind + name) and the agreeing contribution type, then
 * refetches the sort inbox (the NeedsAction/ResolveContributorShape row clears) and the
 * contacts list (every gift from that donor is corrected at once).
 */
export const useResolveContributorShape = defineWrite({
  row: 'Contribution',
  send: async (id, concurrencyVersion: RowVersion, shape: Omit<ResolveContributorShapeRequest, 'concurrencyVersion'>) => {
    const response = await apiResponse(`/api/finance/contributions/${id}/resolve-contributor-shape`, {
      method: 'POST',
      json: { ...shape, concurrencyVersion } satisfies ResolveContributorShapeRequest,
    });
    if (!response.ok) {
      await throwStagedRowError(response, 'Failed to update the donor');
    }
    return response.json() as Promise<ContributionDto>;
  },
});

// Stamp the row "Not reported" (Done/Voided) on click; the write set refreshes the
// aggregates it shifts (contacts, obligations) and the inbox, which drops the row from the inbox queue
// when the Void fires outside the triage modal (StagedDetailPanel).
export const useVoidContribution = defineStatusFlip({
  endpoint: {
    entity: 'Contribution',
    send: async (id, concurrencyVersion) => {
      const response = await apiResponse(`/api/finance/contributions/${id}/void`, {
        method: 'POST',
        json: { concurrencyVersion } satisfies VoidRowRequest,
      });
      if (!response.ok) {
        await throwStagedRowError(response, 'Failed to void contribution');
      }
      return response.json() as Promise<ContributionDto>;
    },
  },
  patch: markVoided,
});

// ADR-016 un-void — restores a Voided contribution to Pending (re-review). The
// inverse of `useVoidContribution`; same POST + body + invalidation set. The
// restore button lives in the cancellation chooser dialog — this hook is its
// data path. Reuses the `StagedRowUpdateError` envelope
// (`StagedRowUpdateNotVoided` → 400, `StagedRowUpdateConcurrencyConflict` → 409).
// Restore instantly (Voided → NeedsAction/Confirm), back in the review queue.
// Same POST + body + invalidation set as the void it reverses.
export const useUnvoidContribution = defineStatusFlip({
  endpoint: {
    entity: 'Contribution',
    send: async (id, concurrencyVersion) => {
      const response = await apiResponse(`/api/finance/contributions/${id}/unvoid`, {
        method: 'POST',
        json: { concurrencyVersion } satisfies VoidRowRequest,
      });
      if (!response.ok) {
        await throwStagedRowError(response, 'Failed to restore contribution');
      }
      return response.json() as Promise<ContributionDto>;
    },
  },
  patch: markPendingReview,
});

// ADR-016 single-row Delete — permanently removes a non-Reported contribution
// (a mis-entry that never happened). Distinct from `useVoidContribution` (the
// soft, reversible cancel): Delete returns 204 (no payload) and the concurrency
// token rides as a query param (DELETE carries no body). Reuses the
// `StagedRowUpdateError` envelope — the backend emits `DeleteErrorCodes`
// (`DeleteHasDependentRefunds` → 409, `DeleteMissingConcurrencyVersion` → 400,
// etc.) in the same `{ code, error }` shape.
export const useDeleteContribution = defineWrite({
  row: 'Contribution',
  send: async (id, concurrencyVersion: RowVersion): Promise<void> => {
    const response = await apiResponse(
      `/api/finance/contributions/${id}?concurrencyVersion=${concurrencyVersion}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      await throwStagedRowError(response, 'Failed to delete contribution');
    }
    // 204 No Content — no payload.
  },
});

// Ledger uncheck (2026-06-12) — POST /contributions/{id}/unconfirm, the inverse
// of useConfirmContribution. Clicking a Done contribution's ✓ moves the row one
// state back (Confirmed → Pending) so the Confirm button returns.
export const useUnconfirmContribution = defineStatusFlip({
  endpoint: {
    entity: 'Contribution',
    send: (id, concurrencyVersion) =>
      apiJson<ContributionDto>(`/api/finance/contributions/${id}/unconfirm`, {
        method: 'POST',
        json: { concurrencyVersion },
        error: refusal('Failed to uncheck contribution'),
      }),
  },
  patch: markPendingReview,
});

// Standalone Pending → Confirmed flip (USI-006). Flips the row's green check the
// instant it's clicked — without the optimistic patch the check appeared only
// after the POST AND a full sort-inbox refetch (two round-trips that read as a
// dead button).
export const useConfirmContribution = defineStatusFlip({
  endpoint: {
    entity: 'Contribution',
    send: (id, concurrencyVersion) =>
      apiJson<ContributionDto>(`/api/finance/contributions/${id}/confirm`, {
        method: 'POST',
        json: { concurrencyVersion },
        error: refusal('Failed to confirm contribution'),
      }),
  },
  patch: markConfirmed,
});

// AL EDI delta-export (design §6): the manual one-click "mark as reported"
// carve-out. A treasurer who entered a contribution directly in the AL FCPA
// portal flips it Confirmed → Reported so our rules see it WITHOUT us ever
// re-exporting it (a re-export would duplicate it on the append-only portal —
// ADR-020). An audited row mutation, not an ExportBatch. The optional `note`
// (e.g. "entered directly in the AL FCPA portal") rides the request; an empty
// note is dropped by JSON.stringify, so the wire omits it. The wire codes come
// from `ContributionMarkReportedErrorCodes`.
export const useMarkContributionReported = defineStatusFlip({
  endpoint: {
    entity: 'Contribution',
    send: (id, concurrencyVersion, { note }: { note?: string }) =>
      apiJson<ContributionDto>(`/api/finance/contributions/${id}/mark-reported`, {
        method: 'POST',
        json: { concurrencyVersion, note },
        error: refusal('Failed to mark this contribution as reported'),
      }),
  },
  patch: markReported,
});

// AL EDI delta-export (design §6): reverse a manual mark
// (Reported → Confirmed), returning the row to the export pool. The audited,
// provenance-gated carve-out of ADR-007 — the backend allows it only when the
// row has no export-upload membership.
export const useUnmarkContributionReported = defineStatusFlip({
  endpoint: {
    entity: 'Contribution',
    send: (id, concurrencyVersion) =>
      apiJson<ContributionDto>(`/api/finance/contributions/${id}/unmark-reported`, {
        method: 'POST',
        json: { concurrencyVersion },
        error: refusal('Failed to undo the mark-reported'),
      }),
  },
  patch: markUnreported,
});

// The triage-time "treasurer asked the donor for occupation / employer; awaiting response"
// hook. POSTs to `/api/finance/contributions/{id}/donor-info-requested` with the channel the
// request went out on, and gets back the updated contribution. The gift's LedgerStatus is
// unchanged; the concurrency token is not, which is why the response is read rather than
// discarded — a double-click re-sends the spent token and conflicts. The write set refreshes the
// inbox, which parks the row out of the queue until the donor info is filled.
export const useDonorInfoRequested = defineWrite({
  row: 'Contribution',
  send: (id, concurrencyVersion: RowVersion, { channel }: { channel: DonorInfoRequestChannel }) =>
    apiJson<ContributionDto>(`/api/finance/contributions/${id}/donor-info-requested`, {
      method: 'POST',
      json: { concurrencyVersion, channel },
      error: refusal('Failed to record donor info request'),
    }),
});

export const useDonorInfoRequests = defineQuery({
  key: (contributionId: string) => ['finance', 'contributions', 'donor-info-requests', contributionId],
  reads: 'GET /api/finance/contributions/{id}/donor-info-request-history',
  fetch: (contributionId, { campaignId }) =>
    apiJson<DonorInfoRequestDto[]>(`/api/finance/contributions/${contributionId}/donor-info-request-history`, {
      headers: campaignHeaders(campaignId),
      error: 'Failed to load donor info request history',
    }),
});

// USI-004 / ADR-011: useKeepAsConfirmed deleted with the adverse-status
// banner machinery.

export const useRedesignateContribution = defineWrite({
  row: 'Contribution',
  send: async (id, concurrencyVersion: RowVersion, redesignation: Omit<RedesignateRequest, 'concurrencyVersion'>) =>
    readCure(
      await cure('redesignate')(id, { ...redesignation, concurrencyVersion } satisfies RedesignateRequest),
      'Failed to redesignate contribution',
    ),
});
